import { cn } from "@/lib/utils";

export function PageHeader({ title, subtitle, icon: Icon, action }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6">
      <div className="flex items-center gap-3">
        {Icon && (
          <div className="grid h-11 w-11 place-items-center rounded-2xl bg-emerald-50 text-emerald-600">
            <Icon size={22} />
          </div>
        )}
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-950">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

export function StatCard({ label, value, sub, icon: Icon, tone = "zinc", onClick, testid }) {
  const tones = {
    zinc: "text-zinc-500", emerald: "text-emerald-600", amber: "text-amber-600", red: "text-red-600",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "text-left w-full rounded-3xl border border-zinc-200 bg-white p-5 shadow-sm transition-transform duration-200",
        onClick && "hover:-translate-y-1 hover:shadow-md active:scale-[0.98]"
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider font-semibold text-zinc-400">{label}</span>
        {Icon && <Icon size={20} className={tones[tone]} />}
      </div>
      <div className="mt-3 font-display text-2xl font-bold text-zinc-950">{value}</div>
      {sub && <div className={cn("mt-1 text-sm", tones[tone])}>{sub}</div>}
    </button>
  );
}

export function EmptyState({ icon: Icon, title, text, action }) {
  return (
    <div className="rounded-3xl border border-dashed border-zinc-300 bg-white/60 p-10 text-center">
      {Icon && (
        <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-zinc-100 text-zinc-400">
          <Icon size={26} />
        </div>
      )}
      <h3 className="font-display text-lg font-semibold text-zinc-900">{title}</h3>
      {text && <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">{text}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function HealthRing({ score = 0, size = 132 }) {
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score));
  const offset = c - (pct / 100) * c;
  const color = pct >= 75 ? "#10B981" : pct >= 50 ? "#F59E0B" : "#EF4444";
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#f0f0f2" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke} fill="none"
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1s ease-out" }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <span className="font-display text-3xl font-extrabold text-zinc-950">{Math.round(pct)}</span>
        <span className="text-[10px] uppercase tracking-wider text-zinc-400 -mt-1">score</span>
      </div>
    </div>
  );
}
